import React, { useEffect, useState, useRef } from "react";
import { AxiosResponse, AxiosError } from "axios";
import { ServiceCodeItem, ServiceCodeResponse } from "../../interface/ServiceCode";
import { ServiceCodeAxiosInstance } from "../../Network/ServiceCodeAxiosInstance";
import "react-toastify/dist/ReactToastify.css";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import DataTable2 from "shared/DataTable2";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";

const ServiceCode: React.FC = () => {
  const [items, setItems] = useState<ServiceCodeResponse>({ result: [] });
  const [code, setCode] = useState<string>("");
  const [codeDescription, setCodeDescription] = useState<string>("");
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  // UI control states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  useEffect(() => {
    fetchItems();
  }, []);

  const clear = () => {
    setCode("");
    setCodeDescription("");
    setSelectedCode(null);
    setIsEdit(false);
  };

  const handleAddClick = () => {
    clear();
    setIsDialogOpen(true);
  };

  const handleEditClick = (item: ServiceCodeItem) => {
    handleEdit(item);
    setIsDialogOpen(true);
  };

  const fetchItems = async (): Promise<void> => {
    try {
      const response: AxiosResponse<{ success: boolean; result: ServiceCodeItem[] }> =
        await ServiceCodeAxiosInstance.get("getServiceCode");
      setItems({ result: [...response.data.result] });
    } catch (error) {
      console.error("Error fetching Service Codes:", error);
      toast({
        title: "Error",
        description: "Failed to fetch Service Codes",
        variant: "destructive",
      });
    }
  };

  const addItem = async (): Promise<void> => {
    if (!code.trim()) {
      toast({
        title: "Validation Error",
        description: "Service code is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        code: code.trim(),
        code_description: codeDescription.trim(),
        token: token,
      };

      const response = await ServiceCodeAxiosInstance.post("addServiceCode", payload);

      if (response.data.success) {
        fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({
          title: "Success",
          description: "Service code added successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.error || "An unexpected error occurred.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error saving Service code:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const updateItem = async (): Promise<void> => {
    if (!selectedCode) return;

    try {
      const payload = {
        oldcode: selectedCode,
        newcode: code.trim(),
        code_description: codeDescription.trim(),
        token: token,
      };

      const response = await ServiceCodeAxiosInstance.put("updateServiceCode", payload);

      if (response.data.success) {
        fetchItems();
        clear();
        setIsDialogOpen(false);
        toast({
          title: "Success",
          description: "Service code updated successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.error || "An unexpected error occurred.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error updating Service code:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: ServiceCodeItem) => {
    setCode(item.code);
    setCodeDescription(item.code_description);
    setIsEdit(true);
    setSelectedCode(item.code);
    setIsDialogOpen(true);
  };

  const removeItem = async (itemCode: string): Promise<void> => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response: AxiosResponse<{ success: boolean; message: string }> =
        await ServiceCodeAxiosInstance.delete(`deleteServiceCode/${itemCode}`, {
          headers,
          data: { token },
        });

      if (response.data.success) {
        fetchItems();
        toast({
          title: "Success",
          description: "Service code deleted successfully.",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete Service code.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error deleting Service code:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to delete Service code.",
        variant: "destructive",
      });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsUploading(true);
      const response = await ServiceCodeAxiosInstance.post("uploadFile", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast({
          title: "Success",
          description: response.data.message || "File uploaded successfully!",
        });
        fetchItems();
      } else {
        toast({
          title: "Error",
          description: response.data.error || "File upload failed",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error uploading file:", error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.error || "Error occurred during upload",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const columns: ColumnDef<ServiceCodeItem>[] = [
    {
      accessorKey: "code",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Service Code
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
    },
    {
      accessorKey: "code_description",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Description
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
            ? " ↓"
            : ""}
        </Button>
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleEdit(row.original)}>
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => removeItem(row.original.code)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-4 pt-16 pb-4">
      <div className="flex justify-between items-center mb-6 mt-4">
        <h5 className="flex items-center gap-2 font-bold text-2xl">
          <span className="icon-container">
            <i className="fa-solid fa-notes-medical"></i>
          </span>
          Service Codes (UAE Abu Dhabi + Dubai)
        </h5>

        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx, .xls"
            style={{ display: "none" }}
          />
          <Button
            variant="outline"
            className="flex items-center gap-2"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="fa-solid fa-file-excel"></i>
            {isUploading ? "Uploading..." : "Upload Excel"}
          </Button>
        </div>
      </div>

      <DataTable2
        columns={columns}
        data={items.result}
        searchColumns={["code", "code_description"]}
        addButtonLabel="Add Service Code"
        showAddButton={true}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEdit ? "Edit Service Code" : "Add Service Code"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="service_code">Service Code *</Label>
              <Input
                id="service_code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. 1, 9, 18.16"
                disabled={isEdit}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="code_description">Description</Label>
              <Input
                id="code_description"
                value={codeDescription}
                onChange={(e) => setCodeDescription(e.target.value)}
                placeholder="Service Description"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={isEdit ? updateItem : addItem}>
              {isEdit ? "Save Changes" : "Add Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
};

export default ServiceCode;
